import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSpocPremiseService } from "../apis/spocPremise/useSpocPremiseService";
import {
  TSpoc,
  TCreateSpocRequest,
  TUpdateSpocRequest,
  TPremise,
  TCreatePremiseRequest,
  TUpdatePremiseRequest,
} from "../apis/spocPremise/spocPremise.interfaces";
import { useInstituteContext } from "../context/institute.context";
import { useAuthUserState } from "../context/authUser.context";
import { getInstituteIdForRequest } from "../utils/apiHelpers";

export const useSpocHook = () => {
  const { getSpocs } = useSpocPremiseService();
  const { selectedInstitute } = useInstituteContext();
  const { userDetails } = useAuthUserState();

  // Automatically get institute ID - for restricted users, uses assigned institute; for standard users, uses selected institute
  const instituteId = getInstituteIdForRequest(userDetails, selectedInstitute);

  return useQuery<TSpoc[]>({
    queryKey: ["spocs", instituteId],
    queryFn: () => getSpocs(instituteId),
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateSpocHook = () => {
  const { createSpoc } = useSpocPremiseService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TSpoc, Error, TCreateSpocRequest>({
    mutationFn: (request: TCreateSpocRequest) => createSpoc(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["spocs", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateSpocHook = () => {
  const { updateSpoc } = useSpocPremiseService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TSpoc, Error, { id: string; request: TUpdateSpocRequest }>(
    {
      mutationFn: ({ id, request }) => updateSpoc(id, request),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["spocs", selectedInstitute?.id],
        });
      },
    },
  );
};

export const useDeleteSpocHook = () => {
  const { deleteSpoc } = useSpocPremiseService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteSpoc(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["spocs", selectedInstitute?.id],
      });
    },
  });
};

export const usePremiseHook = () => {
  const { getPremises } = useSpocPremiseService();
  const { selectedInstitute } = useInstituteContext();
  const { userDetails } = useAuthUserState();

  // Automatically get institute ID - for restricted users, uses assigned institute; for standard users, uses selected institute
  const instituteId = getInstituteIdForRequest(userDetails, selectedInstitute);

  return useQuery<TPremise[]>({
    queryKey: ["premises", instituteId],
    queryFn: () => getPremises(instituteId),
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const usePremiseByIdHook = (id?: string) => {
  const { getPremiseById } = useSpocPremiseService();

  return useQuery<TPremise>({
    queryKey: ["premise", id],
    queryFn: () => getPremiseById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreatePremiseHook = () => {
  const { createPremise } = useSpocPremiseService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TPremise, Error, TCreatePremiseRequest>({
    mutationFn: (request: TCreatePremiseRequest) => createPremise(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["premises", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdatePremiseHook = () => {
  const { updatePremise } = useSpocPremiseService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TPremise,
    Error,
    { id: string; request: TUpdatePremiseRequest }
  >({
    mutationFn: ({ id, request }) => updatePremise(id, request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["premises", selectedInstitute?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["premise", data.id] });
    },
  });
};

export const useDeletePremiseHook = () => {
  const { deletePremise } = useSpocPremiseService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<void, Error, string>({
    mutationFn: (id: string) => deletePremise(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["premises", selectedInstitute?.id],
      });
    },
  });
};

export const useArchivePremiseHook = () => {
  const { archivePremise } = useSpocPremiseService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TPremise, Error, string>({
    mutationFn: (id: string) => archivePremise(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["premises", selectedInstitute?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["premise", data.id] });
    },
  });
};
